import type {List} from 'immutable';
import {useMemo, useState} from 'react';

import {resource} from '@albert-io/json-api-framework/request/builder';
import type {ClassroomModelV1, SubjectModelV2, TeacherModelV1} from '@albert-io/models';
import {getTeacherQuery} from 'client/Classrooms/Classroom/Teacher/Subjects/TeacherSubjectsList.react';
import {getActiveClassroomsWithSubjectsQuery} from 'client/generic/SubjectMenuRenderer/helpers/queries';
import masqueradeStore from 'generic/Masquerade/Masquerade.store';
import useMandarkQueries from 'lib/hooks/useMandarkQueries';

// The teacher's classrooms with their subjects — the source for the aggregated class-subject grid.
// Mirrors the legacy SubjectLibrary/ClassSubjects query.
const subjectsInClassroomsQuery = () =>
  resource('classrooms_v1')
    .mandarkEndpoint(['teachers_v1', masqueradeStore.getUserIdByMasqueradeState(), 'classrooms_v1'])
    .include('subjects_v2.curriculum_area_v1')
    .include('subjects_v2.guides_v1')
    .pageSize(250);

export interface ClassworkSubjects {
  isReady: boolean;
  /** The teacher has no classrooms at all. */
  hasNoClassrooms: boolean;
  /** Distinct subjects across all classrooms, sorted by name, filtered by the Show-hidden toggle. */
  subjects: SubjectModelV2[];
  /** A hidden subject exists in the aggregate — gates the Filters control. */
  hasHiddenSubjects: boolean;
  showHidden: boolean;
  setShowHidden: (show: boolean) => void;
  /** Feeds SubjectCourseCard's Practice Settings dropdown (per-class open/close toggles). */
  activeClassrooms: List<ClassroomModelV1> | undefined;
  /** Licensed teacher at a licensed school — gates the per-card Practice Settings dropdown. */
  isUserLicensedTeacher: boolean;
}

export const useClassworkSubjects = (): ClassworkSubjects => {
  const classroomsQuery = useMemo(() => subjectsInClassroomsQuery(), []);
  const activeClassroomsQuery = useMemo(() => getActiveClassroomsWithSubjectsQuery(), []);
  const teacherQuery = useMemo(() => getTeacherQuery(), []);
  const {isReady, resources} = useMandarkQueries([
    classroomsQuery,
    activeClassroomsQuery,
    teacherQuery
  ]);

  const classrooms = resources[0] as List<ClassroomModelV1> | undefined;
  const activeClassrooms = resources[1] as List<ClassroomModelV1> | undefined;
  const teacher = resources[2] as TeacherModelV1 | undefined;

  const [showHidden, setShowHidden] = useState(false);

  const allSubjects = useMemo<SubjectModelV2[]>(() => {
    if (!isReady || !classrooms) {
      return [];
    }
    const seen = new Set<string>();
    const aggregated: SubjectModelV2[] = [];
    classrooms.forEach((classroom) => {
      classroom.getSubjects().forEach((subject) => {
        if (!seen.has(subject.getId())) {
          seen.add(subject.getId());
          aggregated.push(subject);
        }
      });
    });
    return aggregated.sort((a, b) => a.getName().localeCompare(b.getName()));
  }, [isReady, classrooms]);

  const hasHiddenSubjects = allSubjects.some((subject) => subject.isHidden());
  const subjects = showHidden ? allSubjects : allSubjects.filter((subject) => !subject.isHidden());

  // isReady does NOT guarantee the school_v5 relationship is populated: teachers without a school
  // (a supported account state) get an empty Immutable Map stub with no getId. Guard with
  // hasSchool() and treat no-school as unlicensed. See HB fault 129713607.
  const isUserLicensedTeacher = useMemo(() => {
    if (!isReady || !teacher || !teacher.hasSchool()) {
      return false;
    }
    const schoolId = teacher.getSchool().getId();
    return teacher.getMeta().isLicensed() && teacher.getLicenseStatusForSchool(schoolId);
  }, [isReady, teacher]);

  return {
    isReady,
    hasNoClassrooms: isReady && (!classrooms || classrooms.isEmpty()),
    subjects,
    hasHiddenSubjects,
    showHidden,
    setShowHidden,
    activeClassrooms,
    isUserLicensedTeacher
  };
};
